/**
 * Where an exaggerated vertex reads the painter view's residual warp, packed into the flag of
 * aExaggeration so the attribute keeps its two floats; painterProjection.glsl decodes it.
 *
 * 0 leaves a vertex unexaggerated and 1 exaggerates it about its own foot, which suits a wall that
 * runs too far for one anchor. From 2 up the code carries an object's centre on a one-metre grid of
 * world x and z, and every vertex of a tower or gate moves by the field at that centre's foot: read
 * at each vertex's own foot, the field narrowed the south-east towers by 12 to 38%. Integers below
 * 2^24 are exact in a 32-bit float, which bounds the grid to 2047 m either side of the origin; the
 * fortifications stand within 900 m of it.
 *
 * A city wall's vertex is anchored at its own foot too, and its code also carries the direction the
 * wall's outer face turns to, as a fraction of a turn between 1.25 and 1.45: the painter view gives
 * a wall the whole exaggeration only where that face turns to the painter (wallExaggeration.ts). A
 * 32-bit float there resolves the direction to 4 microradians.
 *
 * A part the painter drew at a fixed fraction of the tower exaggeration is anchored at its own foot
 * and carries that share between 1.55 (none of it) and 1.95 (all of it): the Oude Kerk's tower body
 * is painted about 2.0 times its height, not the 3.54 of the Schreierstoren, and the church and
 * town-hall bodies 2 to 3.3 times (landmarks' measurements on the 6692 px scan, round 17). A 32-bit
 * float there resolves the share to 3e-7.
 */

import type { Vec2 } from '../geo/types';

export const ANCHOR = {
  none: 0,
  foot: 1,
  firstFacing: 1.25,
  facingSpan: 0.2,
  firstShare: 1.55,
  shareSpan: 0.4,
  centreFloor: 1.975,
  firstCentre: 2,
  stepM: 1,
  span: 4096,
  offset: 2047,
} as const;

/* Written so that NaN fails the test too, instead of packing a NaN flag whose decode no GPU
   defines. */
function gridIndex(coordinate: number): number {
  const index = Math.round(coordinate / ANCHOR.stepM) + ANCHOR.offset;
  if (!(index >= 0 && index <= 2 * ANCHOR.offset)) {
    throw new RangeError(
      `An anchor at ${String(coordinate)} m lies off the ${String(ANCHOR.offset)} m anchor grid`
    );
  }
  return index;
}

/** The code of a layout point (x east, y north), whose world z is -y. */
export function centreAnchorCode(centre: Vec2): number {
  return ANCHOR.firstCentre + gridIndex(centre[0]) * ANCHOR.span + gridIndex(-centre[1]);
}

/** The world x and z a code carries, or null for the codes without a centre. */
export function anchorCentre(code: number): Vec2 | null {
  if (code < ANCHOR.firstCentre) return null;
  const packed = code - ANCHOR.firstCentre;
  const column = Math.floor(packed / ANCHOR.span);
  const row = packed - column * ANCHOR.span;
  return [(column - ANCHOR.offset) * ANCHOR.stepM, (row - ANCHOR.offset) * ANCHOR.stepM];
}

/** The foot code of a wall vertex whose outer face turns to `outward`, a unit direction in world x
 *  and z. */
export function facingFootCode(outward: Vec2): number {
  const turn = Math.atan2(outward[1], outward[0]) / (2 * Math.PI);
  return ANCHOR.firstFacing + (turn < 0 ? turn + 1 : turn) * ANCHOR.facingSpan;
}

/** The world x and z direction a wall vertex's code carries, or null for the codes without one. */
export function anchorOutward(code: number): Vec2 | null {
  if (code < ANCHOR.firstFacing - ANCHOR.facingSpan / 2) return null;
  if (code > ANCHOR.firstCentre - 0.5) return null;
  const angle = ((code - ANCHOR.firstFacing) / ANCHOR.facingSpan) * 2 * Math.PI;
  return [Math.cos(angle), Math.sin(angle)];
}

/** The foot code of a part that takes the fixed `share`, 0 to 1, of the painter view's exaggeration. */
export function shareFootCode(share: number): number {
  if (!(share >= 0 && share <= 1)) {
    throw new RangeError(`An exaggeration share of ${String(share)} lies outside 0 to 1`);
  }
  return ANCHOR.firstShare + share * ANCHOR.shareSpan;
}

/** The fixed share a code carries, or null for the codes without one. */
export function anchorShare(code: number): number | null {
  if (!(code > ANCHOR.firstCentre - 0.5 && code < ANCHOR.centreFloor)) return null;
  return Math.min(1, Math.max(0, (code - ANCHOR.firstShare) / ANCHOR.shareSpan));
}
